import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useGamificationService } from '../services/gamificationService';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import type { LeaderboardEntry } from '../types';

const Leaderboard: React.FC = () => {
  const service = useGamificationService();
  const [players, setPlayers] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    service.getLeaderboard()
      .then((data) => setPlayers(Array.isArray(data) ? data : []))
      .catch((err: unknown) => {
        showSnackbar(err instanceof Error ? err.message : 'Failed to load leaderboard', 'error');
        setPlayers([]);
      })
      .finally(() => setLoading(false));
  }, [service, showSnackbar]);

  if (loading) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (players.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No leaderboard data yet.
      </Typography>
    );
  }

  return (
    <>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Team Leaderboard
        </Typography>
        <List disablePadding dense>
          {players.map((player, index) => (
            <ListItem
              key={player.player_id}
              disablePadding
              sx={{ py: 0.5, borderBottom: index < players.length - 1 ? 1 : 0, borderColor: 'divider' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 24, textAlign: 'right' }}
                >
                  {player.rank}.
                </Typography>
                <ListItemText
                  primary={player.display_name}
                  secondary={`Level ${player.level}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                  sx={{ flex: 1 }}
                />
                <Chip
                  icon={<StarIcon />}
                  label={player.total_xp.toLocaleString()}
                  size="small"
                  variant="outlined"
                  color={index === 0 ? 'warning' : 'default'}
                />
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>
      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </>
  );
};

export default Leaderboard;
